// ============================================================
// TILT — useTiltEngine
// ------------------------------------------------------------
// The ONE bridge between React and the physics. Every screen
// that runs a table uses this hook and nothing else.
//
// ── THE TWO-CLOCK PROBLEM ────────────────────────────────────
// The engine wants to run at a fixed 120Hz forever.
// React wants to re-render as rarely as possible.
// These are not the same clock, and merging them is the single
// most common way to destroy a browser game's performance.
//
// So we DON'T merge them:
//
//   • The engine ticks on requestAnimationFrame, every frame,
//     forever. It lives in a useRef. React does not see it.
//   • The CANVAS draws from the engine directly, on its own rAF.
//     It also does not go through React.
//   • React re-renders ONLY when the HUD's numbers change —
//     throttled to ~20Hz via a snapshot tick. A score counter
//     does not need 120 updates a second. Nobody can read that.
//
// Result: physics at 120Hz, canvas at 60Hz, React at 20Hz, and
// the tab stays cool. If you ever find yourself putting engine
// state into useState, stop, and come back and read this.
// ============================================================

import { useRef, useEffect, useState, useCallback } from "react";
import { TiltEngine, PHASE } from "./TiltEngine";
import { getPower } from "./Powers";
import { typing } from "./tiltOnline";   // the typing law: never act on keys while a name is being typed

const SNAP_HZ = 20;    // how often React sees new numbers

export function useTiltEngine(table, opts = {}) {
  const engineRef = useRef(null);
  const rafRef    = useRef(0);
  const lastRef   = useRef(0);
  const snapAccRef = useRef(0);
  const [snap, setSnap] = useState(null);
  const [gen, setGen]   = useState(0);        // bump to hard-restart

  // Keep the latest callbacks without re-creating the engine.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // ── Build (and rebuild) the engine ─────────────────────────
  useEffect(() => {
    if (!table) return;
    // A table carries mutable mode state (_state). Reset it, or a
    // second playthrough inherits the first one's lit targets.
    if (table._init) table._init();
    else table._state = null;

    const e = new TiltEngine(table, {
      seed: optsRef.current.seed,
      owner: optsRef.current.owner,
      loadout: optsRef.current.loadout,
      // "ai" (default): the boss drives itself. "remote": a human
      // opponent drives it over the SabotageBus — PvP duels only.
      bossDriven: optsRef.current.bossDriven,
      onEvent: (ev) => optsRef.current.onEvent?.(ev),
    });
    engineRef.current = e;
    optsRef.current.onReady?.(e);     // QUICK CO-OP: a relay ball restores the shared table here, before the first frame
    setSnap(e.snapshot());

    return () => { engineRef.current = null; };
  }, [table, gen]);

  // ── The frame loop ─────────────────────────────────────────
  useEffect(() => {
    const loop = (t) => {
      rafRef.current = requestAnimationFrame(loop);
      const e = engineRef.current;
      if (!e) return;

      const last = lastRef.current || t;
      const dt = Math.min((t - last) / 1000, 0.25);
      lastRef.current = t;

      // Plunger auto-charge while held
      if (e._plungerHeld && e.phase === PHASE.READY) e.pullPlunger(dt);

      e.tick(dt);

      // Throttled snapshot → React
      snapAccRef.current += dt;
      if (snapAccRef.current >= 1 / SNAP_HZ) {
        snapAccRef.current = 0;
        setSnap(e.snapshot());
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [gen]);

  // ── Input ──────────────────────────────────────────────────
  const press = useCallback((side, down) => {
    engineRef.current?.press(side, down);
  }, []);

  const plungerDown = useCallback(() => {
    const e = engineRef.current;
    if (e) e._plungerHeld = true;
  }, []);
  const plungerUp = useCallback(() => {
    const e = engineRef.current;
    if (!e) return;
    e._plungerHeld = false;
    e.release();
  }, []);

  const cast = useCallback((id, arg) => {
    const e = engineRef.current;
    if (!e) return null;
    const fx = e.cast(id, arg);
    setSnap(e.snapshot());     // immediate feedback — don't wait for the tick
    return fx;
  }, []);

  const beginAim = useCallback((id) => {
    const e = engineRef.current;
    if (!e) return;
    // "sab:powerid" is the sabotage-aim namespace (duel screens).
    if (id.startsWith("sab:")) {
      e.aiming = id;
    } else {
      e.beginAim(id);
    }
    setSnap(e.snapshot());
  }, []);

  const cancelAim = useCallback(() => {
    const e = engineRef.current;
    if (!e) return;
    e.aiming = null;
    e._aimHover = null;
    setSnap(e.snapshot());
  }, []);

  const restart = useCallback(() => {
    lastRef.current = 0;
    setGen((g) => g + 1);
  }, []);

  // ── Keyboard ───────────────────────────────────────────────
  // Left/Right flippers, Space = plunger, 1-5 = powers, N = nudge.
  useEffect(() => {
    if (opts.keyboard === false) return;
    const down = (ev) => {
      const e = engineRef.current;
      if (!e) return;
      if (ev.repeat) return;
      if (typing(ev)) return;   // a name / password being typed, or a Mark / sign-in card open: never flick a flipper
      switch (ev.code) {
        case "ArrowLeft": case "KeyA": case "ShiftLeft":
          press("L", true); ev.preventDefault(); break;
        case "ArrowRight": case "KeyD": case "ShiftRight":
          press("R", true); ev.preventDefault(); break;
        case "Space":
          if (e.phase === PHASE.READY) { e._plungerHeld = true; ev.preventDefault(); }
          break;
        case "Escape":
          cancelAim(); break;
        case "KeyN": case "ArrowUp":
          cast("nudge", { x: 0, y: -1 }); ev.preventDefault(); break;
        case "Digit1": case "Digit2": case "Digit3": case "Digit4": {
          const i = parseInt(ev.code.slice(5), 10) - 1;
          const id = e.loadout[i];
          if (!id) break;
          const p = getPower(id);
          if (p?.target === "point") beginAim(id);
          else cast(id);
          break;
        }
        default: break;
      }
    };
    const up = (ev) => {
      const e = engineRef.current;
      if (!e) return;
      switch (ev.code) {
        case "ArrowLeft": case "KeyA": case "ShiftLeft":
          press("L", false); break;
        case "ArrowRight": case "KeyD": case "ShiftRight":
          press("R", false); break;
        case "Space":
          if (e.phase === PHASE.READY) { e._plungerHeld = false; e.release(); }
          break;
        default: break;
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [press, cast, beginAim, cancelAim, opts.keyboard]);

  return {
    engineRef, snap,
    press, cast, beginAim, cancelAim, restart,
    plungerDown, plungerUp,
  };
}

export default useTiltEngine;
